import { Build, Campaign, CardMembership, CompareArrows, ConfirmationNumber, Flag, Group, Handshake, Inventory, ManageAccounts, MoneyOff, Payment, PhotoCamera, PrecisionManufacturing, Receipt, RequestQuote, SelectAll, ShoppingCart, Storefront, SystemUpdateAlt, VideoLibrary, ViewColumn, ViewList, Widgets, WorkOutline, AccountBalanceWallet } from '@mui/icons-material';
import CalculateIcon from '@mui/icons-material/Calculate';
import DashboardIcon from '@mui/icons-material/Dashboard';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import { Box, Button, Divider, Drawer, ListSubheader, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import PremiumFeatureBadge from './PremiumFeatureBadge';
import { isMasterUser } from '../helpers/authSession';
import { readCachedFeatures, resolveMenuItemState } from '../helpers/featureFlags';
import { buildPaywallUrl } from '../helpers/paywall';
import { refreshSessionContext } from '../helpers/sessionContext';

const APP_VERSION = String(process.env.REACT_APP_VERSION || '').trim();

function pathMatches(pathname, path, matchPaths = [], exact = false) {
    if (pathname === path) {
        return true;
    }
    if (exact) {
        return false;
    }
    if (matchPaths.some((prefix) => pathname.startsWith(prefix))) {
        return true;
    }
    return false;
}

function SidebarNavItem({ item, pathname, darkMode, onClose, premiumLocked, nested = false, exact = false }) {
    const navigate = useNavigate();
    const active = pathMatches(pathname, item.path, item.matchPaths, exact);
    const activeColor = darkMode ? 'yellow' : 'blue';

    const handleClick = (event) => {
        if (premiumLocked) {
            event.preventDefault();
            navigate(buildPaywallUrl({ returnTo: item.path, featureKey: item.featureFlag }));
        }
        onClose?.();
    };

    return (
        <Button
            color="inherit"
            component={Link}
            to={item.path}
            startIcon={React.cloneElement(item.icon, { fontSize: nested ? 'small' : 'large' })}
            onClick={handleClick}
            sx={{
                justifyContent: 'flex-start',
                width: '100%',
                textTransform: 'none',
                color: active ? activeColor : 'inherit',
                fontSize: '1.05rem',
                fontWeight: active ? 700 : 400,
                py: nested ? 0.75 : 1,
                pl: nested ? 4 : 1,
                '&:hover': {
                    backgroundColor: darkMode
                        ? 'rgba(255, 255, 0, 0.1)'
                        : 'rgba(0, 0, 255, 0.08)',
                },
            }}
        >
            <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center' }}>
                {item.label}
                {premiumLocked ? <PremiumFeatureBadge /> : null}
            </Box>
        </Button>
    );
}

const MENU_SECTIONS = [
    {
        id: 'gerencial',
        title: 'Gerencial',
        items: [
            { path: '/admin', label: 'Dashboard', icon: <DashboardIcon /> },
            { path: '/admin/tutorials', label: 'Tutoriais', icon: <OndemandVideoIcon /> },
            { path: '/admin/calculator', label: 'Calculadora de preço', icon: <CalculateIcon /> },
        ],
    },
    {
        id: 'operacional',
        title: 'Operacional',
        items: [
            {
                path: '/admin/printers',
                label: 'Impressoras',
                icon: <SelectAll />,
                matchPaths: ['/admin/register-printer', '/admin/edit-printer'],
            },
            { path: '/admin/jobs', label: 'Impressões', icon: <WorkOutline />, matchPaths: ['/admin/register-job', '/admin/edit-job'] },
            {
                path: '/admin/printer-maintenances',
                label: 'Manutenções',
                icon: <Build />,
                matchPaths: ['/admin/register-printer-maintenance', '/admin/edit-printer-maintenance'],
            },
            {
                path: '/admin/replacement-parts',
                label: 'Peças de reposição',
                icon: <Inventory />,
                matchPaths: ['/admin/register-replacement-part', '/admin/edit-replacement-part'],
            },
            { path: '/admin/materials', label: 'Filamentos', icon: <Widgets />, matchPaths: ['/admin/register-material', '/admin/edit-material'] },
        ],
    },
    {
        id: 'comercial',
        title: 'Comercial',
        items: [
            { path: '/admin/products', label: 'Produtos', icon: <Storefront />, matchPaths: ['/admin/register-product', '/admin/edit-product'] },
            { path: '/admin/customers', label: 'Clientes', icon: <Group />, matchPaths: ['/admin/register-customer', '/admin/edit-customer'] },
            {
                path: '/admin/partners',
                label: 'Parcerias',
                icon: <Handshake />,
                featureFlag: 'commerce_partners',
                matchPaths: ['/admin/partners/'],
            },
            {
                path: '/admin/quick-checkout',
                label: 'Quick Checkout',
                icon: <PhotoCamera />,
                featureFlag: 'quick_checkout',
            },
            {
                path: '/admin/quotes',
                label: 'Orçamentos',
                icon: <RequestQuote />,
                featureFlag: 'quotes',
                matchPaths: ['/admin/register-quote', '/admin/edit-quote', '/admin/quotes/'],
            },
            {
                path: '/admin/orders',
                label: 'Pedidos',
                icon: <ShoppingCart />,
                matchPaths: [
                    '/admin/orders/',
                    '/admin/register-checkout',
                    '/admin/register-batch-checkout',
                    '/admin/edit-checkout',
                ],
                children: [
                    { path: '/admin/orders', label: 'Quadro de pedidos', icon: <ViewColumn />, exact: true },
                    { path: '/admin/orders/list', label: 'Lista', icon: <ViewList /> },
                ],
            },
        ],
    },
    {
        id: 'financeiro',
        title: 'Financeiro',
        items: [
            { path: '/admin/transactions', label: 'Transações', icon: <Receipt />, featureFlag: 'finance', matchPaths: ['/admin/create-transaction', '/admin/edit-transaction'] },
            {
                path: '/admin/operational-costs',
                label: 'Custos operacionais',
                icon: <AccountBalanceWallet />,
                featureFlag: 'operational_costs',
                matchPaths: ['/admin/register-operational-cost', '/admin/edit-operational-cost'],
            },
            {
                path: '/admin/settlement-import',
                label: 'Conciliação Bancária',
                icon: <CompareArrows />,
                featureFlag: 'bank_reconciliation',
            },
        ],
    },
    {
        id: 'administrativo',
        title: 'Administrativo',
        items: [
            {
                path: '/admin/tickets',
                label: 'Tickets',
                icon: <ConfirmationNumber />,
                masterOnly: true,
                matchPaths: ['/admin/tickets/new', '/admin/tickets/'],
            },
            {
                path: '/admin/mp-subscriptions',
                label: 'Assinaturas MP',
                icon: <Payment />,
                masterOnly: true,
            },
            {
                path: '/admin/subscription-billing-exemptions',
                label: 'Isenções',
                icon: <MoneyOff />,
                masterOnly: true,
            },
            {
                path: '/admin/users',
                label: 'Usuários',
                icon: <ManageAccounts />,
                masterOnly: true,
                matchPaths: ['/admin/register-user', '/admin/edit-user', '/admin/users/'],
            },
            {
                path: '/admin/notifications',
                label: 'Notificações',
                icon: <Campaign />,
                masterOnly: true,
            },
            {
                path: '/admin/feature-flags',
                label: 'Feature flags',
                icon: <Flag />,
                masterOnly: true,
            },
            {
                path: '/admin/subscription-plans',
                label: 'Planos',
                icon: <CardMembership />,
                masterOnly: true,
                matchPaths: ['/admin/subscription-plans/new', '/admin/subscription-plans/'],
            },
            {
                path: '/admin/desktop-releases',
                label: 'Versões Desktop',
                icon: <SystemUpdateAlt />,
                masterOnly: true,
                matchPaths: ['/admin/desktop-releases/new', '/admin/desktop-releases/'],
            },
            {
                path: '/admin/tutorial-videos',
                label: 'Vídeos tutoriais',
                icon: <VideoLibrary />,
                masterOnly: true,
                matchPaths: ['/admin/tutorial-videos/new', '/admin/tutorial-videos/'],
            },
            {
                path: '/admin/printer-models',
                label: 'Modelos de impressora',
                icon: <PrecisionManufacturing />,
                masterOnly: true,
                matchPaths: ['/admin/printer-models/new', '/admin/printer-models/'],
            },
        ],
    },
];

const Sidebar = ({ open, onClose, darkMode }) => {
    const location = useLocation();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [features, setFeatures] = useState(readCachedFeatures);

    useEffect(() => {
        const onUserUpdated = (event) => {
            if (typeof event?.detail?.is_master === 'boolean') {
                setIsMaster(event.detail.is_master);
            }
        };
        const onFeaturesUpdated = (event) => {
            if (event?.detail && typeof event.detail === 'object') {
                setFeatures(event.detail);
            }
        };
        window.addEventListener('acompanha:user-updated', onUserUpdated);
        window.addEventListener('acompanha:features-updated', onFeaturesUpdated);
        return () => {
            window.removeEventListener('acompanha:user-updated', onUserUpdated);
            window.removeEventListener('acompanha:features-updated', onFeaturesUpdated);
        };
    }, []);

    useEffect(() => {
        if (!localStorage.getItem('access_token')) {
            return undefined;
        }
        let cancelled = false;
        refreshSessionContext()
            .then(() => {
                if (cancelled) {
                    return;
                }
                setIsMaster(isMasterUser());
                setFeatures(readCachedFeatures());
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, []);

    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            variant="temporary"
            sx={{
                '& .MuiDrawer-paper': {
                    width: 280,
                    boxSizing: 'border-box',
                    top: 64,
                    height: 'calc(100% - 64px)',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    px: 1,
                    pt: 1,
                    pb: 1,
                    flex: 1,
                    minHeight: 0,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}
            >
                {MENU_SECTIONS.map((section, index) => {
                    const items = section.items
                        .map((item) => ({
                            item,
                            state: resolveMenuItemState(item, { isMaster, features }),
                        }))
                        .filter(({ state }) => state.visible);
                    if (items.length === 0) {
                        return null;
                    }
                    return (
                        <Box key={section.id}>
                            {index > 0 && <Divider sx={{ my: 1 }} />}
                            <ListSubheader
                                component="div"
                                sx={{
                                    lineHeight: 2,
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                    letterSpacing: '0.08em',
                                    bgcolor: 'transparent',
                                    color: darkMode ? 'grey.400' : 'text.secondary',
                                    px: 1.5,
                                }}
                            >
                                {section.title}
                            </ListSubheader>
                            {items.map(({ item, state }) => (
                                <Box key={item.path}>
                                    <SidebarNavItem
                                        item={item}
                                        pathname={location.pathname}
                                        darkMode={darkMode}
                                        onClose={onClose}
                                        premiumLocked={state.premiumLocked}
                                    />
                                    {(item.children || []).map((child) => (
                                        <SidebarNavItem
                                            key={`${item.path}-${child.label}`}
                                            item={child}
                                            pathname={location.pathname}
                                            darkMode={darkMode}
                                            onClose={onClose}
                                            premiumLocked={state.premiumLocked}
                                            nested
                                            exact={child.exact}
                                        />
                                    ))}
                                </Box>
                            ))}
                        </Box>
                    );
                })}
            </Box>
            {APP_VERSION ? (
                <Box
                    sx={{
                        px: 2,
                        py: 1,
                        flexShrink: 0,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                    }}
                >
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            color: darkMode ? 'grey.600' : 'text.disabled',
                            fontSize: '0.65rem',
                            letterSpacing: '0.02em',
                            userSelect: 'text',
                        }}
                    >
                        {APP_VERSION}
                    </Typography>
                </Box>
            ) : null}
        </Drawer>
    );
};

export default Sidebar;
